import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { HydratedStoryPoint } from 'magical-trails';

import { closeStorySelector } from '../../store/trail';
import { RootState } from '../../store';
import { getSavedStory } from '../../utils/localStorage';
import { selectStoryFromSelector } from '../../utils/getCode';
import Modal from '../Modal/Modal';

function isScannedPoint(
  point: HydratedStoryPoint
): point is HydratedStoryPoint & { scanned: string } {
  return 'scanned' in point && typeof point.scanned === 'string';
}

function getProgressLabel(storyCode: string) {
  const storyProgress = getSavedStory(storyCode);

  if (!storyProgress) {
    return 'Not started';
  }

  const scannedPoints = storyProgress.storyPoints.filter(isScannedPoint).length;

  return `${scannedPoints} of ${storyProgress.storyPoints.length} points scanned`;
}

const StorySelector: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { storySelector, currentStory } = useSelector(
    (state: RootState) => state.trail
  );

  if (!storySelector) {
    return null;
  }

  return (
    <Modal
      isOpen
      handleClose={() => {
        dispatch(closeStorySelector());
      }}
    >
      <div className="w-full max-w-md">
        <h2 className="text-mt-ink text-lg font-semibold">Choose a story</h2>
        <p className="text-mt-ink-muted mt-2 text-sm">
          This trail has more than one story. Pick the one you want to play.
        </p>
        <div className="mt-5 space-y-2">
          {storySelector.stories.map((story) => {
            const isCurrentStory = currentStory?.code === story.code;

            return (
              <button
                key={story.code}
                type="button"
                className="border-mt-border-soft disabled:bg-mt-surface-2 hover:border-mt-primary hover:bg-mt-primary-soft flex w-full items-center justify-between gap-4 rounded-md border px-4 py-3 text-left shadow-xs disabled:cursor-default"
                disabled={isCurrentStory && !storySelector.scannedCode}
                onClick={() => {
                  selectStoryFromSelector(storySelector, story.code, history);
                  dispatch(closeStorySelector());
                }}
              >
                <span>
                  <span className="text-mt-ink block text-sm font-semibold">
                    {story.title}
                  </span>
                  <span className="text-mt-ink-muted mt-1 block text-xs">
                    {getProgressLabel(story.code)}
                  </span>
                </span>
                {isCurrentStory && (
                  <span className="bg-mt-primary-soft text-mt-primary shrink-0 rounded-full px-2 py-1 text-xs font-medium">
                    Current
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </Modal>
  );
};

export default StorySelector;
